import React, { FunctionComponent } from 'react'
import { Theme, useGetSelectedTheme } from '@afterpaytouch/core'
import clsx from 'clsx'

export const KINDS = {
  LIGHT: 'Light',
  DARK: 'Dark',
  SUBTLE: 'Subtle',
} as const

export const TYPES = {
  HOLLOW: 'Hollow',
  FILLED: 'Filled',
} as const

export const SIZES = {
  SMALL: 'Small',
  LARGE: 'Large',
} as const

type KindsType = typeof KINDS
type KindsKeys = keyof KindsType
type DotIconKinds = KindsType[KindsKeys]

type TypesType = typeof TYPES
type TypesKeys = keyof TypesType
type DotIconTypes = TypesType[TypesKeys]

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
type DotIconSizes = SizesType[SizesKeys]

interface DotIconProps {
  kind?: DotIconKinds
  type?: DotIconTypes
  size?: DotIconSizes
  testNameSpace?: string
}

export const DotThemeVariantMapping: {
  [K in Theme]: {
    size: {
      small: string
      large: string
    }
  }
} = {
  [Theme.Cash]: {
    size: {
      small: 'h-2 w-2',
      large: 'h-3 w-3',
    },
  },
  [Theme.Pegasus]: {
    size: {
      small: 'h-2 w-2',
      large: 'h-3 w-3',
    },
  },
  [Theme.AP]: {
    size: {
      small: 'h-4 w-4',
      large: 'h-6 w-6',
    },
  },
} as const

export const DotIcon: FunctionComponent<DotIconProps> = ({ kind = KINDS.LIGHT, type = TYPES.FILLED, size = SIZES.SMALL, testNameSpace = '' }) => {
  const theme = useGetSelectedTheme()
  const themeMapping = DotThemeVariantMapping[theme]
  const sizeMapping = size === SIZES.SMALL ? themeMapping.size.small : themeMapping.size.large
  const isFilled = type === TYPES.FILLED

  const filledStyleMap: Record<DotIconKinds, string> = {
    Dark: `${isFilled ? 'bg-dark' : ''} border-dark`,
    Subtle: `${isFilled ? 'bg-dark-subtle' : ''} border-dark-subtle`,
    Light: isFilled ? 'bg-light' : '',
  }

  const filledStyle = filledStyleMap[kind] ?? ''

  return (
    <div
      {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-dot-icon` })}
      className={clsx(sizeMapping, filledStyle, 'relative rounded-full border-2 border-solid')}
    />
  )
}
